import React from 'react';
import { Client } from '@twilio/conversations';
import {
  Clickable,
  Dot,
  Icon,
  P,
  useRelativeTime,
} from '@fashionnovateam/nova-ui-toolkit';
import cn from 'classnames';
import {
  ChatConversation,
  useChatContext,
  useConversationById,
  useLastMessage,
} from '../../../Chat';
import { getReorderChatId } from '../../RequestedItemsDrawer/ChatTab/utils';

export type ReorderLatestMessageProps = {
  vendorId: string;
  styleColor: string;
  onClick?: () => void;
  fallbackContent?: string;
  fallbackUpdatedAt?: string;
};

const CellClasses =
  'flex h-full w-full px-3 py-2 items-center flex-row gap-3 rounded-lg';

function FallbackMessage(props: {
  content: string;
  updatedAt?: string;
  onClick?: () => void;
}) {
  const { content, updatedAt, onClick } = props;

  const t = useRelativeTime(updatedAt ? new Date(updatedAt) : 0);

  const preview = content.length > 49 ? `${content.slice(0, 49)}...` : content;

  return (
    <Clickable
      className={cn(CellClasses, 'hover:bg-gray-100')}
      onClick={onClick}
    >
      <Dot color="grey" className="flex-shrink-0" />
      <div className="flex w-[calc(100%-12px-8px)] h-full flex-col gap-0">
        <P size="xs" className="text-start text-ellipsis overflow-hidden">
          {preview}
        </P>
        {updatedAt && (
          <P
            color="disabled"
            size="xs"
            className="text-start text-ellipsis overflow-hidden"
          >
            {t}
          </P>
        )}
      </div>
    </Clickable>
  );
}

function ViewRequestCell(props: { onClick?: () => void }) {
  return (
    <Clickable
      className={cn(CellClasses, 'hover:bg-gray-100')}
      onClick={props.onClick}
    >
      <Dot color="grey" />
      <P
        size="xs"
        color="disabled"
        className="text-center text-ellipsis overflow-hidden"
      >
        View Request
      </P>
    </Clickable>
  );
}

function resolveFallback(params: {
  fallbackContent?: string;
  fallbackUpdatedAt?: string;
  onClick?: () => void;
  defaultFallback: React.ReactElement;
}): React.ReactElement {
  const { fallbackContent, fallbackUpdatedAt, onClick, defaultFallback } =
    params;

  if (fallbackContent) {
    return (
      <FallbackMessage
        content={fallbackContent}
        updatedAt={fallbackUpdatedAt}
        onClick={onClick}
      />
    );
  }
  return defaultFallback;
}

function ReorderLatestMessageConversation(props: {
  conversation: ChatConversation;
  onClick?: () => void;
  fallbackContent?: string;
  fallbackUpdatedAt?: string;
}) {
  const { conversation, onClick, fallbackContent, fallbackUpdatedAt } = props;
  const { userId } = useChatContext();

  const isJoined = conversation.instance.status === 'joined';

  const lastMessageProcess = useLastMessage(
    userId,
    isJoined ? conversation.instance : undefined,
  );

  const fallbackDate = fallbackUpdatedAt ? new Date(fallbackUpdatedAt) : 0;
  const relativeTimeInput = isJoined
    ? conversation.instance.lastMessage?.dateCreated ||
      conversation.instance.dateCreated ||
      0
    : fallbackDate;
  const t = useRelativeTime(relativeTimeInput);

  if (!isJoined) {
    return resolveFallback({
      fallbackContent,
      fallbackUpdatedAt,
      onClick,
      defaultFallback: <ViewRequestCell onClick={onClick} />,
    });
  }

  if (lastMessageProcess.isLoading) {
    return resolveFallback({
      fallbackContent,
      fallbackUpdatedAt,
      onClick,
      defaultFallback: (
        <Clickable
          className={cn(CellClasses, 'justify-center')}
          onClick={onClick}
        >
          <Icon.LoadingCircle />
        </Clickable>
      ),
    });
  }

  if (!lastMessageProcess.data) {
    return resolveFallback({
      fallbackContent,
      fallbackUpdatedAt,
      onClick,
      defaultFallback: (
        <Clickable
          className={cn(CellClasses, 'hover:bg-gray-100')}
          onClick={onClick}
        >
          <Dot color="grey" />
          <P
            size="xs"
            color="disabled"
            className="text-center text-ellipsis overflow-hidden"
          >
            No messages
          </P>
        </Clickable>
      ),
    });
  }

  const textPreview =
    lastMessageProcess.data?.attributes.type === 'tiptap'
      ? lastMessageProcess.data.attributes.preview
      : lastMessageProcess.data?.body || '';

  const preview =
    textPreview.trim().length === 0
      ? `${lastMessageProcess.data?.files.length} files attached`
      : textPreview.trim();

  const isUnread =
    conversation.instance.lastMessage?.index != null &&
    (conversation.instance.lastReadMessageIndex == null ||
      conversation.instance.lastReadMessageIndex <
        conversation.instance.lastMessage.index);

  return (
    <Clickable
      className={cn(CellClasses, {
        'bg-[#F0F3FF]': isUnread,
        'hover:bg-gray-100': !isUnread,
      })}
      onClick={onClick}
    >
      <Dot color={isUnread ? 'primary' : 'grey'} className="flex-shrink-0" />
      <div className="flex w-[calc(100%-12px-8px)] h-full flex-col gap-0">
        <P size="xs" className="text-start text-ellipsis overflow-hidden">
          {preview.length > 49 ? `${preview.slice(0, 49)}...` : preview}
        </P>
        <P
          color="disabled"
          size="xs"
          className="text-start text-ellipsis overflow-hidden"
        >
          {t}
        </P>
      </div>
    </Clickable>
  );
}

function ReorderLatestMessageConnected(props: {
  client: Client;
  vendorId: string;
  styleColor: string;
  onClick?: () => void;
  fallbackContent?: string;
  fallbackUpdatedAt?: string;
}) {
  const {
    client,
    vendorId,
    styleColor,
    onClick,
    fallbackContent,
    fallbackUpdatedAt,
  } = props;
  const { userId: chatUserId } = useChatContext();

  const conversationProcess = useConversationById(
    chatUserId,
    client,
    'reorder',
    getReorderChatId(vendorId, styleColor),
  );

  if (conversationProcess.isLoading) {
    return resolveFallback({
      fallbackContent,
      fallbackUpdatedAt,
      onClick,
      defaultFallback: <ViewRequestCell onClick={onClick} />,
    });
  }

  if (conversationProcess.error || !conversationProcess.data) {
    return resolveFallback({
      fallbackContent,
      fallbackUpdatedAt,
      onClick,
      defaultFallback: <ViewRequestCell onClick={onClick} />,
    });
  }

  return (
    <ReorderLatestMessageConversation
      conversation={conversationProcess.data}
      onClick={onClick}
      fallbackContent={fallbackContent}
      fallbackUpdatedAt={fallbackUpdatedAt}
    />
  );
}

export function ReorderLatestMessage({
  vendorId,
  styleColor,
  onClick,
  fallbackContent,
  fallbackUpdatedAt,
}: ReorderLatestMessageProps) {
  const { client, error, status } = useChatContext();

  if (!client || error || status !== 'connected') {
    return resolveFallback({
      fallbackContent,
      fallbackUpdatedAt,
      onClick,
      defaultFallback: (
        <Clickable
          className={cn(CellClasses, 'hover:bg-gray-100')}
          onClick={onClick}
        >
          <P
            size="xs"
            color="disabled"
            className="text-center text-ellipsis overflow-hidden"
          >
            Not connected to Chat
          </P>
        </Clickable>
      ),
    });
  }

  return (
    <ReorderLatestMessageConnected
      client={client}
      vendorId={vendorId}
      styleColor={styleColor}
      onClick={onClick}
      fallbackContent={fallbackContent}
      fallbackUpdatedAt={fallbackUpdatedAt}
    />
  );
}
